import React, { useEffect, useState } from 'react'

import { centerMatchesSpecializations } from '../../domain/useCases/score/centerMatchesSpecializations'
import { adjustPacing } from '../../domain/useCases/score/adjustPacing'
import { adjustSponsorTier } from '../../domain/useCases/score/adjustSponsorTier'
import { getMatchedSearchTerm } from '../../domain/useCases/score/getMatchedSearchTerm'
import { boostLocalSponsorForCityLocations } from '../../domain/useCases/score/boostLocalSponsorForCityLocations'
import { adjustPrimaryFocus } from '../../domain/useCases/score/adjustPrimaryFocus'
import {
  FOCUS_BOOST,
  PROVIDER_TYPES_TO_BOOST,
  QUALITY_REDUCE_RATIO
} from '../../domain/useCases/score/constants'
import { isNonEmptyString } from '../../utils/checks'
import {
  formatAdminLink,
  listSegmentName,
  getAdOpsCookie
} from '../../domain/useCases/adminBarInfo/adminBarUtils'
import { isNull } from '../../utils/checks'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faInfoCircle, faPenToSquare } from '@fortawesome/free-solid-svg-icons'
import AdminInfoBrowse from './AdminInfoBrowse'
import { getProviderType } from '../../domain/center'
import { centerProviderTypeMatchesLevelOfCare } from '../../domain/useCases/score/centerProviderTypeMatchesLevelOfCare'
import {
  isLevelsOfCareFacetPageType,
  isLevelsOfCarePageType,
  isLocationPageType
} from '../../domain/useCases/pageTypeCheck'
import { handleAdCenterPriority } from '../../domain/useCases/score/handleAdCenterPriority'
import { QUERY_DEBUG_PARAM } from '../../domain/useCases/adminBarInfo/constants'
import { isResidentialProvider } from '../../utils/providerTypeChecks'
import { shouldBoostResidentialProvider } from '../../domain/useCases/score/shouldBoostResidentialProvider'
import { boostMultiSearchCenter } from '../../domain/useCases/score/boostMultiSearchCenter'
const toolTipStyle = 'text-xs mx-1'
const PRIMARY_FOCUS_MATCH_MULTIPLIER = 1

const AdminBarInfo = ({
  center,
  activeFacets,
  zoneId,
  position,
  locationType,
  termFocus,
  pageType,
  searchState
}) => {
  const handleFromBrowse = () => !!center?.fromBrowseSearch

  const [isDebugMode, setIsDebugMode] = useState(false)
  const [fromBrowse, setFromBrowse] = useState(handleFromBrowse())
  const [matchedPrimaryFocusRatio, setMatchedPrimaryFocusRatio] = useState(
    PRIMARY_FOCUS_MATCH_MULTIPLIER
  )

  const adjustPrimaryFocusRatio = (primaryFocusRatio) =>
    primaryFocusRatio === FOCUS_BOOST
      ? PRIMARY_FOCUS_MATCH_MULTIPLIER
      : primaryFocusRatio

  useEffect(() => {
    if (QUERY_DEBUG_PARAM.test(window?.location?.search.toString())) {
      setIsDebugMode(true)
    }
    if (getAdOpsCookie()) {
      setIsDebugMode(true)
    }
    if (center?.fromBrowseSearch) {
      setFromBrowse(true)
    }
    return () => {
      setIsDebugMode(false)
    }
  }, [activeFacets, zoneId])

  useEffect(() => {
    if (
      !fromBrowse &&
      isNonEmptyString(center?.primaryFocus) &&
      isNonEmptyString(termFocus)
    ) {
      const primaryFocusRatio = adjustPrimaryFocus({
        center,
        primaryFocus: termFocus
      })

      setMatchedPrimaryFocusRatio(adjustPrimaryFocusRatio(primaryFocusRatio))
    }
  }, [center?.primaryFocus, termFocus])

  if (!isDebugMode) {
    return null
  }
  const shouldDisplayNonBrowseScore = () =>
    activeFacets &&
    !!centerMatchesSpecializations(
      center.new_specializations,
      getMatchedSearchTerm(activeFacets)
    ) &&
    !fromBrowse

  const providerType = getProviderType(center)

  const providerTypeMatchesLevelOfCare =
    centerProviderTypeMatchesLevelOfCare(
      providerType,
      getMatchedSearchTerm(activeFacets)
    ) && PROVIDER_TYPES_TO_BOOST.includes(providerType)

  return (
    <div
      className={`db-rating-value flex flex-col p-1${
        fromBrowse ? 'w-full' : ''
      }`}
      id={center?.id || center?.slug}
    >
      <div className="flex w-full flex-row items-center justify-between gap-x-2 pl-2">
        <div className="group relative after:hidden after:hover:block">
          <FontAwesomeIcon
            className="stroke-primary-400 text-base"
            icon={faInfoCircle}
          />

          <div className="absolute bottom-full mb-2 hidden rounded bg-gray-200 px-2 py-1 text-xs text-gray-700 group-hover:block">
            <div className="flex flex-col whitespace-nowrap text-start">
              <p>sort explanation: </p>
              {center?.priority && (
                <span>
                  +{handleAdCenterPriority(center)}{' '}
                  <span className={`${toolTipStyle}`}>
                    boost for cascading priority
                  </span>
                </span>
              )}
              {shouldDisplayNonBrowseScore() ? (
                <span>
                  +
                  {centerMatchesSpecializations(
                    center.new_specializations,
                    getMatchedSearchTerm(activeFacets)
                  )}{' '}
                  <span className={`${toolTipStyle}`}>
                    boost for {getMatchedSearchTerm(activeFacets)}
                  </span>
                </span>
              ) : (
                <span>
                  +
                  {boostMultiSearchCenter(
                    center.new_specializations,
                    searchState
                  )}{' '}
                  <span className={`${toolTipStyle}`}>
                    boost for matching specialization(s)
                  </span>
                </span>
              )}
              {center?.sponsor_tier && (
                <span className="pr-1">
                  +{adjustSponsorTier(center.sponsor_tier)}
                  <span className={`${toolTipStyle}`}>for sponsor tier</span>
                </span>
              )}
              {shouldBoostResidentialProvider(center) &&
                isLocationPageType(pageType) && (
                  <span>
                    +{shouldBoostResidentialProvider(center)}
                    <span className={`${toolTipStyle}`}>
                      for residential provider
                    </span>
                  </span>
                )}
              {locationType === 'city' &&
                boostLocalSponsorForCityLocations(
                  center.sponsor_tier,
                  locationType
                ) && (
                  <span>
                    +{' '}
                    {boostLocalSponsorForCityLocations(
                      center.sponsor_tier,
                      locationType
                    )}
                    for City Location with sponsor tier 3
                  </span>
                )}
              {!isNull(center?.budget_pacing) && (
                <span className="inline whitespace-nowrap pr-1">
                  {adjustPacing(center?.budget_pacing)}{' '}
                  <span className={`${toolTipStyle}`}>subtract adRank</span>
                </span>
              )}
              {center?.quality_score > 0 && (
                <span className="mr-1">
                  +{center.quality_score * QUALITY_REDUCE_RATIO}
                  <span className={`${toolTipStyle}`}>
                    rough quality score added value
                  </span>
                </span>
              )}
              {center?.qualityScore > 0 && (
                <span>
                  +{center.qualityScore * QUALITY_REDUCE_RATIO}
                  <span className={`${toolTipStyle}`}>
                    rough quality score added value
                  </span>
                </span>
              )}
              {isNonEmptyString(center?.primaryFocus) &&
                isNonEmptyString(termFocus) &&
                center.primaryFocus.includes(termFocus) && (
                  <span>
                    +{adjustPrimaryFocus({ center, primaryFocus: termFocus })}
                    <span className={`${toolTipStyle}`}>
                      for matching primary focus
                    </span>
                  </span>
                )}
              <br />
              {center?.score && (
                <span className={`${toolTipStyle}`}>
                  net score: {center?.score}
                </span>
              )}{' '}
              {center?._rankingInfo?.filters && (
                <div>
                  <span className={`${toolTipStyle}`}>
                    Browse Filter Score: {center?._rankingInfo?.filters}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        <span className="center-name text-sm text-primary-700">
          {center?.title}
        </span>
        {!isNull(position) && (
          <div className="card-position sticky right-2 pr-2 pt-2 text-primary-700">
            {/* position + 1 is added due to ad ops not using zeroeth index, and this increment being added in other locations. */}
            <div className="flex flex-col">
              <span className="mr-1">
                Placement:{' '}
                <span className="position-value">{position + 1}</span>
              </span>
            </div>
          </div>
        )}
      </div>
      {fromBrowse && (
        <AdminInfoBrowse
          center={center}
          termFocus={termFocus}
          position={position}
        />
      )}
      {!fromBrowse && (
        <div className="rating-value-row order-1 inline-flex flex-row justify-around gap-x-2 text-sm">
          {!isNull(center?.budget_pacing) && (
            <div className="flex-col flex-wrap md:flex-row">
              <span className="flex underline">adRank</span>
              <span className="pacing-value mx-0.5 flex">
                {center?.budget_pacing}
              </span>
            </div>
          )}
          {isNonEmptyString(termFocus) && (
            <div
              className={`hidden flex-col flex-wrap sm:flex ${
                center?.primaryFocus?.includes(termFocus)
                  ? 'ring-offset-opacity-50 ring-opacity/50 bg-[#34d399] text-white ring ring-[#34d399] ring-offset-2 ring-offset-[#34d399]'
                  : 'ring-offset-opacity-50 ring-opacity/50 bg-[#f87171] text-white ring ring-[#f87171]  ring-offset-2 ring-offset-[#f87171]'
              }`}
            >
              <span className="mr-0.5 justify-center whitespace-nowrap text-center underline">
                Primary Focus
              </span>
              <span className={`focus-match-value mx-0.5 text-ellipsis`}>
                {center?.primaryFocus?.includes(termFocus)
                  ? `👍 ${termFocus}`
                  : `👎 ${termFocus}`}
              </span>
            </div>
          )}

          {(isLevelsOfCarePageType(pageType) ||
            isLevelsOfCareFacetPageType(pageType)) && (
            <div
              className={`hidden flex-col flex-wrap border-2 p-0.5 sm:flex ${
                providerTypeMatchesLevelOfCare
                  ? 'border-[#34d399] text-[#34d399] '
                  : 'border-[#f87171] text-[#f87171]'
              }`}
            >
              <span className="justify-center whitespace-nowrap text-center underline">
                Provider Type
              </span>
              <span className={`focus-match-value mx-0.5 text-ellipsis`}>
                {providerTypeMatchesLevelOfCare
                  ? `${providerType} (+${providerTypeMatchesLevelOfCare})`
                  : `${providerType}`}
              </span>
            </div>
          )}

          {center?.sponsor_tier && (
            <div className="sponsor-tier mx-0.5 hidden flex-col sm:flex ">
              <span className="mr-0.5 whitespace-nowrap underline">
                sponsor tier:{' '}
                <span className="sponsor-tier-value font-semibold no-underline">
                  {center.sponsor_tier}{' '}
                </span>
              </span>
              <span className="sponsor-tier-boost mx-0.5 text-center font-semibold">
                {' '}
                +{adjustSponsorTier(center.sponsor_tier)}
              </span>{' '}
            </div>
          )}

          {centerMatchesSpecializations(
            center.new_specializations,
            getMatchedSearchTerm(activeFacets)
          ) > 0 && (
            <div className="hidden flex-col flex-wrap sm:flex">
              <span className="underline">matched term</span>
              <span className="font-semibold">
                {getMatchedSearchTerm(activeFacets)}
                <span className="specialization-value mx-0.5">
                  (+
                  {centerMatchesSpecializations(
                    center.new_specializations,
                    getMatchedSearchTerm(activeFacets)
                  )}
                  )
                </span>
              </span>
            </div>
          )}

          {center?.quality_score > 0 ||
            (center?.qualityScore > 0 && (
              <div className="mx-0.5  hidden flex-col flex-wrap items-center whitespace-break-spaces  sm:flex ">
                <span className="mr-0.5 text-clip whitespace-nowrap underline">
                  quality score
                </span>
                <span className="quality-score-value font-semibold">
                  {center.qualityScore || center.quality_score}
                </span>
              </div>
            ))}

          {center?.score && (
            <div className={`flex flex-col text-center md:text-left `}>
              <span
                className={`sorting-score-value ml-0.5 inline-flex whitespace-break-spaces underline sm:max-w-[200px] md:whitespace-nowrap`}
              >
                score
              </span>
              <span className={`sorting-score-value 'font-semibold`}>
                {center?.score?.toFixed(4)}
              </span>
            </div>
          )}

          {center?.feedType && !center?.placedInFeed && (
            <div className="zone-id-container flex flex-col flex-wrap text-start ">
              <span className="zone-id-number text-sm underline ">
                Feed type & Zone Id
              </span>
              <span className="px-1 text-sm font-semibold">
                {center.feedType} - ({listSegmentName(zoneId)})
              </span>{' '}
            </div>
          )}
          {center?.feedType && center?.placedInFeed && (
            <div className="zone-id-container flex flex-col flex-wrap text-start ">
              <span className="zone-id-number text-sm underline ">
                Feed type:
                <span className="feed-type-value"> {center.feedType} </span>
              </span>
              <span className="whitespace-nowrap px-1 text-sm">
                mixed into
                <span className="pl-0.5 font-semibold capitalize">
                  ( {center?.placedInFeed} )
                </span>{' '}
              </span>{' '}
            </div>
          )}
          {isNonEmptyString(center?.slug) && (
            <div className="admin-link-button hidden max-h-5 flex-row items-center justify-end sm:flex">
              {' '}
              <a
                className="edit-center hover:shadow-md flex flex-row px-2 text-primary-800 no-underline hover:rounded-sm"
                href={formatAdminLink(center)}
                target="_blank"
                rel="noreferrer"
              >
                <FontAwesomeIcon icon={faPenToSquare} />
                <span className="ml-2">Edit</span>
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default AdminBarInfo
